import { useState, useEffect, useCallback } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Send, MessageSquare, Plus, ArrowLeft, Shield, ShieldAlert, Star } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { PullToRefreshIndicator } from "@/components/ui/pull-to-refresh-indicator";
import SlaBadge from "@/components/support/SlaBadge";

const CATEGORIES = [
  { value: "billing", label: "Billing" },
  { value: "technical_issue", label: "Technical Issue" },
  { value: "proposal_generator", label: "Proposal Generator" },
  { value: "account_access", label: "Account Access" },
  { value: "other", label: "Other" },
];

interface Ticket {
  id: string;
  subject: string;
  category: string;
  status: string;
  created_at: string;
  updated_at: string;
  rating: number | null;
  rating_comment: string | null;
}

interface Message {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_type?: string | null;
  message: string;
  is_internal_note: boolean;
  created_at: string;
}

const statusColor = (s: string) => {
  switch (s) {
    case "open": return "bg-info/20 text-info border-info/30";
    case "in_progress": return "bg-warning/20 text-warning border-warning/30";
    case "resolved": return "bg-success/20 text-success border-success/30";
    default: return "";
  }
};

const Support = () => {
  const { user, subscription } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [supportAccessGranted, setSupportAccessGranted] = useState<boolean | null>(null);
  const [togglingAccess, setTogglingAccess] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);

  // Form state
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  const [messageText, setMessageText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch support access grant status
  const fetchSupportAccess = useCallback(async () => {
    if (!user) return;
    const { data } = await (supabase as any)
      .from("support_access_grants")
      .select("is_active")
      .eq("user_id", user.id)
      .maybeSingle();
    setSupportAccessGranted((data as any)?.is_active === true);
  }, [user]);

  useEffect(() => { fetchSupportAccess(); }, [fetchSupportAccess]);

  const handleToggleSupportAccess = async (checked: boolean) => {
    if (!user) return;
    setTogglingAccess(true);
    try {
      if (checked) {
        const { error } = await supabase
          .from("support_access_grants" as any)
          .upsert(
            { user_id: user.id, is_active: true, granted_at: new Date().toISOString(), revoked_at: null },
            { onConflict: "user_id" }
          );
        if (error) throw error;
        setSupportAccessGranted(true);
        toast.success("Support access granted — our team can now view your proposals to help resolve your issue.");
      } else {
        const { error } = await supabase
          .from("support_access_grants" as any)
          .update({ is_active: false, revoked_at: new Date().toISOString() })
          .eq("user_id", user.id);
        if (error) throw error;
        setSupportAccessGranted(false);
        toast.success("Support access revoked.");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update support access.");
    } finally {
      setTogglingAccess(false);
    }
  };

  const fetchTickets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("support_tickets")
      .select("*")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false })
      .limit(50);
    setTickets((data ?? []) as Ticket[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const { pullDistance, refreshing, threshold } = usePullToRefresh({
    onRefresh: fetchTickets,
  });

  const fetchMessages = async (ticketId: string) => {
    setMessagesLoading(true);
    const { data } = await supabase
      .from("ticket_messages")
      .select("*")
      .eq("ticket_id", ticketId)
      .eq("is_internal_note", false)
      .order("created_at", { ascending: true });
    setMessages((data ?? []) as Message[]);
    setMessagesLoading(false);
  };

  const openTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    fetchMessages(ticket.id);
  };

  // Realtime subscription for messages
  useEffect(() => {
    if (!selectedTicket) return;
    const channel = supabase
      .channel(`ticket-${selectedTicket.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "ticket_messages",
        filter: `ticket_id=eq.${selectedTicket.id}`,
      }, (payload) => {
        const newMsg = payload.new as Message;
        if (newMsg.is_internal_note) return;
        setMessages((prev) => (prev.some((m) => m.id === newMsg.id) ? prev : [...prev, newMsg]));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedTicket]);

  const handleSubmitTicket = async () => {
    if (!user || !subject.trim() || !messageText.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);

    // Professional+ users get high priority support
    const priorityTiers = ["professional", "agency", "enterprise", "testing"];
    const ticketPriority = subscription.tier && priorityTiers.includes(subscription.tier) ? "high" : "normal";

    const { data: ticket, error: ticketErr } = await supabase
      .from("support_tickets")
      .insert({ user_id: user.id, subject: subject.trim(), category: category as any, priority: ticketPriority } as any)
      .select()
      .single();

    if (ticketErr || !ticket) {
      toast.error("Failed to create ticket: " + (ticketErr?.message ?? "Unknown error"));
      setSubmitting(false);
      return;
    }

    const { error: msgErr } = await supabase
      .from("ticket_messages")
      .insert({ ticket_id: ticket.id, sender_id: user.id, message: messageText.trim() });

    if (msgErr) {
      toast.error("Ticket created but initial message failed: " + msgErr.message);
    } else {
      toast.success("Support ticket submitted!");
      // Fire-and-forget AI tier-1 auto first response
      supabase.functions.invoke("ai-support-agent", {
        body: { mode: "ticket_reply", ticket_id: ticket.id, message: messageText.trim() },
      }).catch(() => {});
    }

    // Send email notifications (fire and forget)
    const userEmail = user.email || "";
    const userName = user.user_metadata?.full_name || "";
    supabase.functions.invoke("send-email", {
      body: { type: "ticket_created", to: userEmail, userName, ticketId: ticket.id },
    }).catch(() => {});
    // Admin alert - send to a known admin email
    supabase.functions.invoke("send-email", {
      body: { type: "admin_alert_new_ticket", to: "rwlclark@yahoo.com", ticketId: ticket.id, userEmail },
    }).catch(() => {});

    setSubject("");
    setCategory("other");
    setMessageText("");
    setShowForm(false);
    setSubmitting(false);
    fetchTickets();
  };

  const handleReply = async () => {
    if (!user || !selectedTicket || !replyText.trim()) return;
    setSending(true);
    const trimmed = replyText.trim();
    const { data: inserted, error } = await supabase
      .from("ticket_messages")
      .insert({ ticket_id: selectedTicket.id, sender_id: user.id, message: trimmed })
      .select()
      .single();
    if (error) toast.error("Failed to send: " + error.message);
    else {
      setReplyText("");
      if (inserted) {
        setMessages((prev) =>
          prev.some((m) => m.id === (inserted as any).id) ? prev : [...prev, inserted as Message]
        );
      }
      supabase.functions.invoke("ai-support-agent", {
        body: { mode: "ticket_reply", ticket_id: selectedTicket.id, message: trimmed },
      }).catch(() => {});
    }
    setSending(false);
  };

  const handleSubmitRating = async () => {
    if (!user || !selectedTicket || ratingValue === 0) return;
    setSubmittingRating(true);
    try {
      const { error } = await supabase
        .from("support_tickets")
        .update({
          rating: ratingValue,
          rating_comment: ratingComment.trim() || null,
          rated_at: new Date().toISOString(),
        } as any)
        .eq("id", selectedTicket.id)
        .eq("user_id", user.id);
      if (error) throw error;
      setSelectedTicket({ ...selectedTicket, rating: ratingValue, rating_comment: ratingComment.trim() || null });
      toast.success("Thank you for your feedback!");
    } catch (err: any) {
      toast.error(err.message || "Failed to submit rating");
    } finally {
      setSubmittingRating(false);
    }
  };

  // Conversation view
  if (selectedTicket) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { setSelectedTicket(null); setMessages([]); }}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-foreground truncate">{selectedTicket.subject}</h1>
              <div className="flex items-center gap-3 mt-1">
                <Badge className={statusColor(selectedTicket.status)} variant="outline">
                  {selectedTicket.status.replace("_", " ")}
                </Badge>
                <SlaBadge ticketId={selectedTicket.id} ticketStatus={selectedTicket.status} size="md" showLabel />
                <span className="text-xs text-muted-foreground capitalize">{selectedTicket.category.replace("_", " ")}</span>
                <span className="text-xs text-muted-foreground">
                  Opened {new Date(selectedTicket.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Support Access Banner */}
          {supportAccessGranted !== null && selectedTicket.status !== "resolved" && (
            <Card className={`border ${supportAccessGranted ? 'border-warning/30 bg-warning/5' : 'border-primary/30 bg-primary/5'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {supportAccessGranted ? (
                      <ShieldAlert className="h-5 w-5 text-warning shrink-0" />
                    ) : (
                      <Shield className="h-5 w-5 text-primary shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-foreground text-sm">
                        {supportAccessGranted ? "Support access is enabled" : "Grant support access to help resolve your issue"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {supportAccessGranted
                          ? "Our team can view your proposals, contracts, and assessments (read-only). You can revoke anytime."
                          : "Allow our team to view your proposals and contracts so they can better assist you."}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={supportAccessGranted}
                    onCheckedChange={handleToggleSupportAccess}
                    disabled={togglingAccess}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-border bg-card shadow-card">
            <CardContent className="p-6">
              {messagesLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : messages.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">No messages yet.</p>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                  {messages.map((msg) => {
                    const isAi = msg.sender_type === "ai_agent";
                    const isMe = !isAi && msg.sender_id === user?.id;
                    const label = isAi ? "AI Support" : isMe ? "You" : "Support";
                    return (
                      <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-xl px-4 py-3 ${
                          isMe
                            ? "bg-primary/15 border border-primary/20"
                            : isAi
                              ? "bg-amber-500/10 border border-amber-500/30"
                              : "bg-secondary border border-border"
                        }`}>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{msg.message}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[10px] text-muted-foreground">{label}</span>
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(msg.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedTicket.status !== "resolved" && (
                <div className="flex items-end gap-3 mt-6 pt-4 border-t border-border">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 bg-secondary border-border text-foreground min-h-[80px]"
                  />
                  <Button onClick={handleReply} disabled={sending || !replyText.trim()} className="gradient-primary text-primary-foreground shadow-glow">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              )}
              {selectedTicket.status === "resolved" && (
                <div className="mt-6 pt-4 border-t border-border space-y-4">
                  <p className="text-center text-sm text-muted-foreground">
                    This ticket has been resolved. Open a new ticket if you need further help.
                  </p>
                  {selectedTicket.rating ? (
                    <div className="text-center space-y-1">
                      <div className="flex items-center justify-center gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`h-5 w-5 ${s <= selectedTicket.rating! ? "text-warning fill-warning" : "text-muted-foreground/30"}`} />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">You rated this support {selectedTicket.rating}/5</p>
                      {selectedTicket.rating_comment && (
                        <p className="text-xs text-muted-foreground italic">"{selectedTicket.rating_comment}"</p>
                      )}
                    </div>
                  ) : (
                    <Card className="border-primary/20 bg-primary/5">
                      <CardContent className="p-4 space-y-3">
                        <p className="text-sm font-medium text-foreground text-center">How was your support experience?</p>
                        <div className="flex items-center justify-center gap-2">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <button
                              key={s}
                              onClick={() => setRatingValue(s)}
                              className="transition-transform hover:scale-110"
                            >
                              <Star className={`h-7 w-7 ${s <= ratingValue ? "text-warning fill-warning" : "text-muted-foreground/30 hover:text-warning/50"}`} />
                            </button>
                          ))}
                        </div>
                        {ratingValue > 0 && (
                          <>
                            <Textarea
                              value={ratingComment}
                              onChange={(e) => setRatingComment(e.target.value)}
                              placeholder="Optional: Tell us more about your experience..."
                              className="bg-secondary border-border text-foreground min-h-[60px]"
                              maxLength={500}
                            />
                            <Button
                              onClick={handleSubmitRating}
                              disabled={submittingRating}
                              className="w-full gradient-primary text-primary-foreground shadow-glow"
                            >
                              {submittingRating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                              Submit Rating
                            </Button>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PullToRefreshIndicator pullDistance={pullDistance} refreshing={refreshing} threshold={threshold} />
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Support Center</h1>
            <p className="text-muted-foreground mt-1">Need help? Our team is here to assist you.</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
            <Plus className="mr-2 h-4 w-4" /> New Ticket
          </Button>
        </div>

        {/* Ticket List */}
        <Card className="border-border bg-card shadow-card">
          <CardHeader>
            <CardTitle className="text-foreground">Your Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : tickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="h-14 w-14 rounded-2xl gradient-accent flex items-center justify-center mb-4">
                  <MessageSquare className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No tickets yet</h3>
                <p className="text-muted-foreground text-center max-w-sm mb-6">
                  Submit a support ticket and our team will respond as soon as possible.
                </p>
                <Button onClick={() => setShowForm(true)} className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
                  <Plus className="mr-2 h-4 w-4" /> Create Ticket
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6">
              <Table className="min-w-[550px]">
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground">Subject</TableHead>
                    <TableHead className="text-muted-foreground">Category</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Last Updated</TableHead>
                    <TableHead className="text-muted-foreground text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.id} className="border-border">
                      <TableCell className="font-medium text-foreground">{ticket.subject}</TableCell>
                      <TableCell className="text-muted-foreground capitalize">{ticket.category.replace("_", " ")}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <Badge className={statusColor(ticket.status)} variant="outline">
                            {ticket.status.replace("_", " ")}
                          </Badge>
                          <SlaBadge ticketId={ticket.id} ticketStatus={ticket.status} />
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(ticket.updated_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openTicket(ticket)}>
                          View Conversation
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Ticket Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="bg-card border-border sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-foreground">Submit a Support Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  className="mt-1 bg-secondary border-border text-foreground"
                  maxLength={200}
                />
              </div>
              <div>
                <Label className="text-muted-foreground">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-1 bg-secondary border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-muted-foreground">Message</Label>
                <Textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Describe your issue in detail..."
                  className="mt-1 bg-secondary border-border text-foreground min-h-[120px]"
                  maxLength={2000}
                />
              </div>
              <Button
                onClick={handleSubmitTicket}
                disabled={submitting || !subject.trim() || !messageText.trim()}
                className="w-full gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Message
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default Support;
